import React, { FunctionComponent, useState } from 'react';
import { Button, FormGroup, Label } from 'reactstrap';
import { ApiDataBinding, ApiDataState, withApiData } from 'react-api-data/lib';
import { compose, withState } from 'recompose';
import { connect } from 'react-redux';
import { array, number, object, string } from 'yup';
import { RouteComponentProps, withRouter } from 'react-router';
import get from 'lodash/get';
import { withTranslation, WithTranslation } from 'react-i18next';

// constants
import { ReduxState } from 'createStore';
import { Roles } from 'constants/literals';

// utils
import { getLinkToConnection, getOnlyOneExchangeName } from 'utils/linksUtils';
import i18n from 'locales/i18next';
import { getExchangeByNameSelector } from 'redux/apiData/apiDataSelectors';

// components
import { ApiResponse, Exchanges, LinkFull, NewLink, ReactApiDataActions } from 'constants/api';
import RolesChecker from 'components/utils/RolesChecker';
import { isCustomer } from 'utils/userUtils';
import ContractsSelectAll from 'components/utils/ContractsSelectAll';
import FormControl from 'components/utils/FormControl';
import FormModal from './FormModal';
import { ExchangeFormikSelect } from 'components/utils/ExchangeSelect';
import PortDataFormGroup from 'components/utils/PortDataFormGroup';

interface InProps {
    contractUuid: string;
}

interface WithApiDataProps {
    exchangeData: ApiDataBinding<Exchanges>;
}

interface ConnectProps {
    apiData: ApiDataState;
    userRoles: Roles[];
    connectionAddRequest: ApiDataBinding<ApiResponse<LinkFull>>;
    apiDataActions: ReactApiDataActions;
}

type EnhanceProps = WithApiDataProps & ConnectProps & RouteComponentProps & WithTranslation;
type Props = InProps & EnhanceProps;

export const createNewLinkBody = (values: FormValues, contractUuid: string): NewLink => {
    const body: NewLink = {
        link: {
            classification: 'link',
            erp_order_identifier: values.orderId,
            sla: values.sla,
            port_count: values.portCount,
            location_uuid: values.location,
            speed_and_interface_types: values.speedAndInterfaceTypes,
            contract_uuid: values.consumingContractUuid,
            managing_contract_uuid: contractUuid,
            oversubscription_percentage: values.oversubscriptionPercentage,
        },
    };
    // when env varable for automated is set, then add it to the body
    const isAutomated = process.env.REACT_APP_AUTOMATED_PROVISIONING_ENABLED;
    if (isAutomated) {
        body.link.automated = isAutomated === 'true';
    }
    return body;
};

const enhance = compose<Props, InProps>(
    withState('targetExchange', 'setTargetExchange', 'nl'),
    withApiData({
        exchangeData: 'getAllExchanges',
        connectionAddRequest: 'postLink',
    }),
    connect((state: ReduxState, props: Props) => ({
        apiData: state.apiData,
    })),
    withRouter,
    withTranslation()
);

type FormValues = {
    exchange: string,
    location: string,
    speedAndInterfaceTypes: string[],
    portCount: number,
    orderId: string,
    sla: boolean,
    consumingContractUuid: string,
    oversubscriptionPercentage: number | undefined,
};

const ConnectionAddModal: FunctionComponent<Props> = ({
    exchangeData,
    connectionAddRequest,
    apiDataActions,
    contractUuid,
    history,
    apiData,
    userRoles,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!exchangeData.data) {
        return <></>;
    }

    const initValues = {
        exchange: getOnlyOneExchangeName(exchangeData.data) || '',
        location: '',
        speedAndInterfaceTypes: [''],
        portCount: 1,
        orderId: '',
        sla: false,
        consumingContractUuid: contractUuid,
        oversubscriptionPercentage: 0,
    };

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit(values: FormValues) {
        const body: NewLink = createNewLinkBody(values, contractUuid);

        return connectionAddRequest.perform({ exchange: values.exchange }, body);
    }

    function onSuccess(binding: ApiDataBinding<LinkFull>) {
        const target = getLinkToConnection(binding.request.result.exchange.short_name, binding.request.result.uuid);
        history.push(target);

        apiDataActions.invalidateCache('getContractLinks', {
            contractUuid,
        });
    }

    const renderNewConnectionBtn = () => {
        return (
            <Button data-test="connection-add-btn" className="float-right" color="light" onClick={toggle}>
                {t('connection.addNewConnectionBtn')}
            </Button>
        );
    };

    return (
        <React.Fragment>
            <FormModal
                header={t('connection.addNewConnection')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={initValues}
                validationSchema={object().shape({
                    exchange: string().required(t('validation.exchangeRequired')),
                    location: string().required(t('validation.locationRequired')),
                    consumingContractUuid: string().required(t('validation.contractRequired')),
                    speedAndInterfaceTypes: array()
                        .min(1, t('validation.portTypeRequired'))
                        .required(t('validation.portTypeRequired')),
                    portCount: number()
                        .min(1, t('validation.portCountRequired'))
                        .required(t('validation.portCountRequired')),
                    orderId: string().required(t('validation.orderIdRequired')),
                    oversubscriptionPercentage: number()
                        .min(0, t('validation.oversubscriptionMin'))
                        .max(100, t('validation.oversubscriptionMax')),
                })}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
                successMessage={t('notification.createConnectionSuccessful')}
            >
                {({ errors, touched, values, setValues, handleBlur }) => (
                    <React.Fragment>
                        <FormGroup>
                            <Label>{t('common.exchangeMandatory')}</Label>
                            <ExchangeFormikSelect
                                name="exchange"
                                touched={touched}
                                errors={errors}
                                exchanges={get(exchangeData, 'data')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('common.locationMandatory')}</Label>
                            <FormControl
                                component="select"
                                name="location"
                                disabled={!values.exchange}
                                errors={errors}
                                touched={touched}
                            >
                                {values.exchange ? (
                                    <>
                                        <option disabled value={''}>
                                            {t('connection.selectLocation')}
                                        </option>
                                        {getExchangeByNameSelector(apiData, values.exchange)
                                            .locations.sort((a, b) => a.description.localeCompare(b.description))
                                            .map((location) => (
                                                <option key={location.uuid} value={location.uuid}>
                                                    {location.description}
                                                </option>
                                            ))}
                                    </>
                                ) : (
                                    <option>{t('connection.selectExchangeOptions')}</option>
                                )}
                            </FormControl>
                        </FormGroup>
                        <FormGroup>
                            <Label>{'*' + t('connection.contractName')}</Label>
                            <ContractsSelectAll
                                onChange={(contract) => {
                                    setValues({ ...values, consumingContractUuid: contract.uuid });
                                }}
                                managingContractUuid={contractUuid}
                                value={values.consumingContractUuid}
                                disabled={!values.exchange}
                                exchange={values.exchange}
                            />
                        </FormGroup>
                        <PortDataFormGroup
                            handleBlur={handleBlur}
                            locationUuid={values.location}
                            errors={errors}
                            touched={touched}
                            onChange={(types) => setValues({ ...values, speedAndInterfaceTypes: types })}
                        />
                        <FormGroup>
                            <Label>{t('connection.portCount')}</Label>
                            <FormControl name="portCount" errors={errors} touched={touched} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('connection.erpOrderID')}</Label>
                            <FormControl name="orderId" errors={errors} touched={touched} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('connection.oversubscriptionPercentage')}</Label>
                            <FormControl name="oversubscriptionPercentage" errors={errors} touched={touched} />
                        </FormGroup>
                    </React.Fragment>
                )}
            </FormModal>
            <React.Fragment>
                <RolesChecker
                    data-test="user-roles-checker"
                    roles={[Roles.admin, Roles.noc, Roles.customer, Roles.ixaas_client]}
                >
                    {isCustomer(userRoles)
                        ? process.env.REACT_APP_CUSTOMER_SHOW_ADD_CONNECTION === 'true' && renderNewConnectionBtn()
                        : renderNewConnectionBtn()}
                </RolesChecker>
            </React.Fragment>
        </React.Fragment>
    );
};

export default enhance(ConnectionAddModal);
